import { useModal } from '@ebay/nice-modal-react'
import { useHistory, useParams } from 'react-router-dom'
import { useGlobalState, useMessage } from '@pepper/ui'

import createOrderSrc from 'assets/images/home/create_order.png'
import createTopicsSrc from 'assets/images/home/create_topics.png'
import exploreOppSrc from 'assets/images/home/explore_opportunities.png'
import createUsingAiSrc from 'assets/images/home/pepper_ai.png'
import createEmptyDocSrc from 'assets/images/home/pepperai_quick_action.png'

import { createPrivateDocument } from 'utils/api/item'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { redirectToNewDoc } from 'utils/functions/redirectToNewDoc'

import s from './CreateContentCards.module.css'

type Card = Record<
  string,
  {
    title: string
    description: string
    image: string
    onClick: () => void
  }
>

type Props = {
  isPtGrandfathered?: boolean
}

const CreateContentCards = ({
  isPtGrandfathered = false,
}: Props): JSX.Element => {
  const history = useHistory()
  const companyId = useSelectedCompanyId()
  const { projectId } = useParams<{ projectId: string }>()
  const { showError } = useMessage()
  const { show: showSelectProjectModal } = useModal('select-project-modal')

  const { permissions } = useGlobalState()
  const { show: showServiceSelectionDrawer } = useModal(
    'service-selection-drawer'
  )
  const { show: showUpsellModal } = useModal('buy-cmp')
  const selectProjectModal = useModal('select-project-modal')

  const handleCreateDoc = async (
    _projectId: string,
    openAiWidget = false
  ): Promise<string | undefined> => {
    const { data, status, error } = await createPrivateDocument({
      projectId: _projectId,
    })
    if (status === 'ok' && data) {
      redirectToNewDoc(data.item_id, openAiWidget, companyId)
    } else {
      showError(error)
      return undefined
    }
  }

  const handleCreateDocClick = async (openAiWidget = false) => {
    if (projectId) {
      handleCreateDoc(projectId, openAiWidget)
    } else {
      showSelectProjectModal({
        onAfterSelectId: async (_projectId: string) => {
          handleCreateDoc(_projectId, openAiWidget)
        },
      })
    }
  }

  const _getCardsOrder = () => {
    if (isPtGrandfathered) {
      return ['create-with-ai', 'new-empty-doc']
    }
    if (permissions?.cmp) {
      return ['create-topics', 'explore-oppotunities', 'create-order']
    } else {
      return ['create-order', 'create-topics', 'explore-oppotunities']
    }
  }

  const cards: Card = {
    'create-topics': {
      title: 'Ideate Topics / Keywords ->',
      description:
        'Recommended for SEO blogs. Discover or upload your own topics and create content in a breeze.',
      image: createTopicsSrc,
      onClick: () => {
        if (!permissions?.topics) {
          showUpsellModal()
        } else {
          selectProjectModal.show({
            onAfterSelectId: (_projectId: string) => {
              history.push(`/project/${_projectId}/topics`)
            },
          })
        }
      },
    },
    'create-order': {
      title: 'Order Expert Content ->',
      description:
        'Only the best! Top 3% of curated experts with high skill and domain competence.',
      image: createOrderSrc,
      onClick: () => {
        showServiceSelectionDrawer()
      },
    },
    'explore-oppotunities': {
      title: 'Audit Existing Website Content ->',
      description:
        'Conduct in-depth analyses to identify gaps, improve rankings, and outperform competitors.',
      image: exploreOppSrc,
      onClick: () => {
        history.push(`/content-refresh`)
      },
    },
  }

  const ptGrandfathered: Card = {
    'create-with-ai': {
      title: 'Create content using AI ->',
      description:
        'Begin the document by directing the AI to commence drafting the content.',
      image: createUsingAiSrc,
      onClick: () => {
        handleCreateDocClick(true)
      },
    },
    'new-empty-doc': {
      title: 'Start from empty Doc ->',
      description: `From a clean slate to a masterpiece: write with or without AI – it's your playground.`,
      image: createEmptyDocSrc,
      onClick: () => {
        handleCreateDocClick(false)
      },
    },
  }

  return (
    <div className={s.container}>
      {_getCardsOrder().map((key: string) => {
        const card = isPtGrandfathered ? ptGrandfathered[key] : cards[key]
        return (
          <button key={key} className={s.card} onClick={card.onClick}>
            <img src={card.image} alt={key} />
            <div className={s.cardText}>
              <h3 className={s.cardTitle}>{card.title}</h3>
              <p className={s.cardDescription}>{card.description}</p>
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default CreateContentCards
